/*
 * Accessibility safety net: ensure no <img> is exposed without an accessible name.
 * Targets images that OMIT the alt attribute (WCAG 1.1.1 / F65) or use a URL as alt
 * (F30). Images intentionally marked decorative with alt="" are left untouched.
 * Covers theme markup at load AND app-injected/dynamic images (Replo, sliders,
 * lazyload) via a MutationObserver. No external data required.
 */
(function () {
  'use strict';

  var SHOP_FALLBACK = null;
  var GENERIC_FALLBACK = SHOP_FALLBACK || 'Product image';

  function clean(s) {
    return (s || '').replace(/\s+/g, ' ').trim();
  }

  // An image needs a fallback only when alt is entirely missing, or alt is a raw URL.
  function needsAlt(img) {
    if (!img.hasAttribute('alt')) return true;
    var a = img.getAttribute('alt');
    if (a && a.indexOf('://') !== -1) return true;
    return false;
  }

  function deriveAlt(img) {
    // 1. Explicit data hook on the image
    if (img.dataset && clean(img.dataset.alt)) return clean(img.dataset.alt);

    // 2. <figure><figcaption>
    var fig = img.closest && img.closest('figure');
    if (fig) {
      var cap = fig.querySelector('figcaption');
      if (cap && clean(cap.textContent)) return clean(cap.textContent);
    }

    // 3. Nearest labelled ancestor (product card links carry aria-label)
    var labelled = img.closest && img.closest('[aria-label]');
    if (labelled && clean(labelled.getAttribute('aria-label'))) {
      return clean(labelled.getAttribute('aria-label'));
    }

    // 4. Product context (card / PDP custom elements)
    var prod = img.closest && img.closest('product-card-atc, product-card, product-main, [data-product-title]');
    if (prod) {
      if (prod.dataset && clean(prod.dataset.productTitle)) return clean(prod.dataset.productTitle);
      var link = prod.querySelector('a.product-card-url[aria-label]');
      if (link && clean(link.getAttribute('aria-label'))) return clean(link.getAttribute('aria-label'));
      var t = prod.querySelector('.title, h1, h2, h3');
      if (t && clean(t.textContent)) return clean(t.textContent);
    }

    // 5. Nearest section heading
    var sec = img.closest && img.closest('section, [class*="shopify-section"]');
    if (sec) {
      var h = sec.querySelector('h1, h2, h3, .title');
      if (h && clean(h.textContent)) return clean(h.textContent);
    }

    // 6. Page title (first segment before a separator)
    var dt = clean((document.title || '').split(/[|–—\-]/)[0]);
    if (dt) return dt;

    // 7. Shop name
    return GENERIC_FALLBACK;
  }

  function fixImage(img) {
    if (needsAlt(img)) {
      var alt = deriveAlt(img);
      if (alt) img.setAttribute('alt', alt);
    }
  }

  // Iframes need a non-empty title (WCAG 4.1.2). Covers app-injected frames
  // (e.g. FrontRow, review widgets) that ship without one.
  function fixIframe(frame) {
    if (frame.hasAttribute('title') && clean(frame.getAttribute('title'))) return;
    var title = '';
    var labelled = frame.closest && frame.closest('[aria-label]');
    if (labelled) title = clean(labelled.getAttribute('aria-label'));
    if (!title) {
      var sec = frame.closest && frame.closest('section, [class*="shopify-section"]');
      var h = sec && sec.querySelector('h1, h2, h3, .title');
      if (h) title = clean(h.textContent);
    }
    if (!title) title = 'Embedded content';
    frame.setAttribute('title', title);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var imgs = root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) fixImage(imgs[i]);
    var frames = root.querySelectorAll('iframe');
    for (var j = 0; j < frames.length; j++) fixIframe(frames[j]);
  }

  function init() {
    scan(document);

    if ('MutationObserver' in window) {
      var obs = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var node = added[n];
            if (node.nodeType !== 1) continue;
            if (node.tagName === 'IMG') {
              fixImage(node);
            } else if (node.tagName === 'IFRAME') {
              fixIframe(node);
            } else {
              scan(node);
            }
          }
        }
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
